import { ApiClient, ApiResponse, BaseApiResponse, HttpResponse } from '@core/swagger-client';
import { ClientType, LiveStatus } from '@core/types';
import { PayloadAction } from '@reduxjs/toolkit';
import { call, getContext, put, select, takeEvery } from 'redux-saga/effects';
import { OnDemandWebinarConnection } from '../signal-r/on-demand-webinar-connection';
import { JoinWebinarRequest } from '../slices/live-webinar-slice';
import { RootState } from '../store';
import {
    OnDemandData,
    OnDemandTimelineEvent,
    onDemandChatMessageAccepted,
    onDemandChatMessageRejected,
    onDemandCurrentVideoTimeSeeded,
    onDemandCurrentVideoTimeUpdated,
    onDemandDataConnectionStatusUpdated,
    onDemandDataUpdated,
    onDemandInitializedWebinar,
    onDemandJoinWebinar,
    onDemandLeaveWebinar,
    onDemandRefreshData,
    onDemandSendChatMessage,
    onDemandSetLastServerAction,
    onDemandTimelineUpdated,
    onDemandVoteOnPoll
} from '../slices/on-demand-webinar-slice';

function* handleOnDemandJoinWebinar(action: PayloadAction<JoinWebinarRequest>) {
    const signalR: OnDemandWebinarConnection = yield getContext('onDemandConnection');
    const api: ApiClient = yield getContext('api');
    signalR.isEditor = action.payload.clientType === ClientType.editor;

    try {
        let result: HttpResponse<ApiResponse<string>> | undefined = undefined;
        if (action.payload.anonymousId) {
            result = yield call(api.anonymous.joinOnDemandWebinar, {
                anonymousId: action.payload.anonymousId,
                authorizationCode: action.payload.anonymousAuthorizationCode,
                webinarId: action.payload.webinarId
            });
        } else if (action.payload.linkCode) {
            result = yield call(api.watchLink.joinOnDemandWebinar, { linkCode: action.payload.linkCode });
        } else {
            result = yield call(
                action.payload.clientType === ClientType.editor ? api.videoEditor.joinWebinar : api.onDemandWebinar.joinWebinar,
                action.payload
            );
        }

        if (result?.data?.success !== true || !result?.data?.data) throw result;
        yield call([signalR, signalR.connect], result.data.data!);
    } catch (error: any) {
        yield put(onDemandDataConnectionStatusUpdated({ status: LiveStatus.Error }));
        let response: BaseApiResponse | undefined = undefined;
        if ('error' in error) {
            response = error.error as BaseApiResponse;
        }
        if ('data' in error) {
            response ??= error.data as BaseApiResponse;
        }
        response ??= {
            statusCode: 500,
            message: 'An unexpected error occurred while joining the webinar.',
            success: false,
            errorCode: 'UnexpectedError'
        };
        yield put(onDemandDataConnectionStatusUpdated({ status: LiveStatus.Error, error: response }));
    }
}

function* handleOnDemandLeaveWebinar() {
    const signalR: OnDemandWebinarConnection = yield getContext('onDemandConnection');
    yield call([signalR, signalR.disconnect]);
}

function* handleOnDemandDataUpdated(action: PayloadAction<OnDemandData>) {
    try {
        if (!action.payload.jsonUrl) {
            // On-demands without a json url are probably not available anymore, but if we throw an error
            // the user will get an internal error instead of a proper "not available" message
            // So we set the timeline to empty and let the frontend handle the "not available" case
            yield put(onDemandTimelineUpdated([]));
        } else {
            const api: ApiClient = yield getContext('api');
            const result: HttpResponse<OnDemandTimelineEvent[]> = yield call(
                api.onDemandWebinar.loadJson,
                action.payload.jsonUrl + '?t=' + new Date().getTime()
            );
            yield put(onDemandTimelineUpdated(result.data));
        }

        // Seed only — NOT onDemandCurrentVideoTimeUpdated. This fires on every (re)join, including
        // an automatic SignalR reconnect mid-playback; forwarding it to the server as a real position
        // would reset the viewer's most-recently-touched session to 0 and corrupt resume (see
        // applyCurrentVideoTime in the slice).
        //
        // Re-seeds the STORE'S OWN current position, not a hardcoded 0: the point is to recompute
        // everything applyCurrentVideoTime derives (active chapter, active slide, chat backlog) against
        // the timeline data that was just (re)loaded above — not to move the position. On the very first
        // join currentVideoTime is naturally still 0, so this behaves the same as before. On a reconnect
        // mid-playback it correctly reflects wherever the viewer actually is; seeding a hardcoded 0
        // there reset the UI to position 0 (slide 1, empty chat, first chapter) for anyone paused, since
        // nothing corrects it again until they resume playback and onTimeUpdate reports the real position.
        const currentVideoTime: number = yield select((state: RootState) => state.onDemandWebinar.currentVideoTime);
        yield put(onDemandCurrentVideoTimeSeeded(currentVideoTime));
        yield put(onDemandInitializedWebinar());
    } catch (error: any) {
        const errorResponse = { statusCode: 500, success: false, message: 'Internal Error' };
        if ('status' in error) {
            errorResponse.statusCode = error.status;
        }

        if ('error' in error) {
            errorResponse.message = error.error?.message || 'Connection error';
        }
        yield put(onDemandDataConnectionStatusUpdated({ status: LiveStatus.Error, error: errorResponse }));
    }
}

function* handleOnDemandRefreshData() {
    try {
        const signalR: OnDemandWebinarConnection = yield getContext('onDemandConnection');
        const result: ApiResponse<OnDemandData> = yield call([signalR, signalR.refreshOnDemandData]);
        yield put(onDemandSetLastServerAction({ method: 'refreshOnDemandData', result }));

        if (result.success && result.data) {
            yield put(onDemandDataUpdated(result.data));
        }
    } catch (error) {
        yield put(
            onDemandSetLastServerAction({
                method: 'refreshOnDemandData',
                result: { statusCode: 500, success: false, message: 'Connection error', errorCode: 'Exception' }
            })
        );
    }
}

function* handleOnDemandCurrentVideoTimeUpdated(action: PayloadAction<number>) {
    try {
        const signalR: OnDemandWebinarConnection = yield getContext('onDemandConnection');

        // If not connected, do not attempt to send the update to the server
        if (!signalR.isConnected) {
            return;
        }

        const result: BaseApiResponse = yield call([signalR, signalR.updateCurrentVideoTime], parseInt(action.payload.toString()));
        yield put(onDemandSetLastServerAction({ method: 'updateCurrentVideoTime', result }));
    } catch (e) {
        yield put(
            onDemandSetLastServerAction({
                method: 'updateCurrentVideoTime',
                result: { statusCode: 500, success: false, message: 'Connection error', errorCode: 'Exception' }
            })
        );
    }
}

function* handleOnDemandVoteOnPoll(action: PayloadAction<{ pollId: string; optionId: string }>) {
    try {
        const signalR: OnDemandWebinarConnection = yield getContext('onDemandConnection');
        const result: BaseApiResponse = yield call([signalR, signalR.voteOnPoll], action.payload.pollId, action.payload.optionId);
        yield put(onDemandSetLastServerAction({ method: 'voteOnPoll', result }));
    } catch (e) {
        yield put(
            onDemandSetLastServerAction({
                method: 'voteOnPoll',
                result: { statusCode: 500, success: false, message: 'Connection error', errorCode: 'Exception' }
            })
        );
    }
}

function* handleOnDemandSendChatMessage(action: PayloadAction<{ channelId: string; message: string }>) {
    try {
        const signalR: OnDemandWebinarConnection = yield getContext('onDemandConnection');
        const result: BaseApiResponse = yield call([signalR, signalR.sendChatMessage], action.payload.channelId, action.payload.message);
        yield put(onDemandSetLastServerAction({ method: 'sendChatMessage', result }));
        // The on-demand hub never echoes a message back: it appears locally only once the server accepted it.
        if (result.success) {
            yield put(onDemandChatMessageAccepted({ channelId: action.payload.channelId, message: action.payload.message }));
        } else {
            yield put(onDemandChatMessageRejected({ channelId: action.payload.channelId, message: action.payload.message, errorCode: result.errorCode }));
        }
    } catch (e) {
        yield put(
            onDemandSetLastServerAction({
                method: 'sendChatMessage',
                result: { statusCode: 500, success: false, message: 'Connection error', errorCode: 'Exception' }
            })
        );
        yield put(onDemandChatMessageRejected({ channelId: action.payload.channelId, message: action.payload.message, errorCode: 'Exception' }));
    }
}

export function* watchOnDemandWebinar() {
    yield takeEvery(onDemandJoinWebinar, handleOnDemandJoinWebinar);
    yield takeEvery(onDemandLeaveWebinar, handleOnDemandLeaveWebinar);
    yield takeEvery(onDemandDataUpdated, handleOnDemandDataUpdated);
    yield takeEvery(onDemandRefreshData, handleOnDemandRefreshData);
    yield takeEvery(onDemandCurrentVideoTimeUpdated, handleOnDemandCurrentVideoTimeUpdated);
    yield takeEvery(onDemandVoteOnPoll, handleOnDemandVoteOnPoll);
    yield takeEvery(onDemandSendChatMessage, handleOnDemandSendChatMessage);
}
